import { Icon } from 'assets/icons';
import React from 'react';
import cx from 'classnames';
import styles from './input.module.css';

/**
 * Shared, presentation-agnostic input. It owns structure + behaviour only —
 * every styleable surface (wrapper, field box, input, label, icon, error) is
 * driven by a className prop so callers and presets can style it to taste
 * without forking the component. SimpleInput / IconInput are thin presets over
 * this base; new looks should come from overrides, not new variants.
 */
const BaseInput = ({
  type = 'text',
  name = '',
  input_id = '',
  value = '',
  placeholder = '',
  disabled,
  min = '',
  max = '',
  onBlur = () => {},
  onInput = () => {},
  onKeyDown = () => {},
  style = {},
  autoFocus = false,
  required = false,
  error = '',
  label = '',
  icon_name = '',
  icon_position = 'left',
  onIconClick = () => {},
  wrapperClassName = '',
  fieldClassName = '',
  inputClassName = '',
  labelClassName = '',
  iconClassName = '',
  errorClassName = ''
}) => {
  const handleInput = (e) => (name ? onInput(e.target) : onInput(e.target.value));

  const renderIcon = (position) =>
    icon_name && icon_position === position ? (
      <span onClick={onIconClick} className={cx(styles.inputIcon, iconClassName)}>
        <Icon width={20} height={20} name={icon_name} />
      </span>
    ) : null;

  return (
    <div className={cx(wrapperClassName)}>
      {label && (
        <label htmlFor={input_id} className={cx(labelClassName)}>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      <div className={cx(fieldClassName)}>
        {renderIcon('left')}
        <input
          type={type}
          name={name}
          id={input_id}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          autoFocus={autoFocus}
          style={style}
          onChange={() => {}}
          onInput={handleInput}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={cx(inputClassName)}
        />
        {renderIcon('right')}
      </div>
      {error && <span className={cx(styles.error, errorClassName)}>{error}</span>}
    </div>
  );
};

export const SimpleInput = ({
  type = 'text',
  label = '',
  name = '',
  input_id = '',
  is_rounded,
  dark = false,
  className = '',
  wrapperClassName = '',
  labelClassName = '',
  errorClassName = '',
  disabled,
  placeholder = '',
  value = '',
  min = '',
  max = '',
  onBlur = () => {},
  onInput = () => {},
  onKeyDown = () => {},
  style = {},
  required = false,
  autoFocus = false,
  error = ''
}) => (
  <BaseInput
    type={type}
    name={name}
    input_id={input_id}
    value={value}
    placeholder={placeholder}
    disabled={disabled}
    min={min}
    max={max}
    onBlur={onBlur}
    onInput={onInput}
    onKeyDown={onKeyDown}
    style={style}
    autoFocus={autoFocus}
    required={required}
    error={error}
    label={label}
    labelClassName={labelClassName}
    errorClassName={errorClassName}
    wrapperClassName={cx(
      styles.formGroup,
      dark && styles.formGroupDark,
      is_rounded && styles.rounded,
      wrapperClassName
    )}
    inputClassName={cx(dark && styles.inputDark, className, { [styles.errorBorder]: error })}
  />
);

export const IconInput = ({
  type = 'text',
  icon_name = '',
  icon_position = 'left',
  is_rounded,
  dark = false,
  label = '',
  input_id = '',
  className = '',
  iconClassName = '',
  wrapperClassName = '',
  labelClassName = '',
  errorClassName = '',
  disabled,
  placeholder = '',
  value = '',
  min = '',
  max = '',
  onBlur = () => {},
  onInput = () => {},
  onIconClick = () => {},
  onKeyDown = () => {},
  style = {},
  autoFocus = false,
  required = false,
  error = ''
}) => {
  const noPadding = icon_position === 'left';
  return (
    <BaseInput
      type={type}
      input_id={input_id}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      onBlur={onBlur}
      onInput={onInput}
      onKeyDown={onKeyDown}
      style={style}
      autoFocus={autoFocus}
      required={required}
      error={error}
      label={label}
      labelClassName={labelClassName}
      errorClassName={cx('mb-4 d-inline-block', errorClassName)}
      icon_name={icon_name}
      icon_position={icon_position}
      onIconClick={onIconClick}
      iconClassName={iconClassName}
      wrapperClassName={cx(dark && styles.formGroupDark)}
      fieldClassName={cx(
        styles.iconFormGroup,
        dark && styles.iconFormGroupDark,
        is_rounded && styles.rounded,
        wrapperClassName,
        { [styles.errorBorder]: error }
      )}
      inputClassName={cx({ [styles.inputIconLeft]: noPadding }, className)}
    />
  );
};
